import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleGoogleCallback, handleAppleCallback, fetchWithAuth, AuthError } from '../lib/auth-api';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        navigate('/login', { replace: true });
        return;
      }

      // Verify state matches
      const savedState = sessionStorage.getItem('oauth_state');
      if (state && savedState && state !== savedState) {
        setError('Invalid OAuth state. Please try again.');
        return;
      }

      try {
        // Determine provider from stored code verifier (Google uses PKCE, Apple does not)
        const codeVerifier = sessionStorage.getItem('oauth_code_verifier');

        let data;
        if (codeVerifier) {
          // Google (uses PKCE code_verifier)
          data = await handleGoogleCallback(code);
        } else {
          // Apple (no code_verifier)
          // Apple sends user name in the initial form_post body
          const userName = searchParams.get('user_name') || undefined;
          data = await handleAppleCallback(code, userName);
        }

        // Decode user info from JWT
        const parts = data.access_token.split('.');
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        setAuthenticated({ id: payload.sub, email: payload.email });

        // Check if user has an entreprise
        try {
          const API_URL = import.meta.env.VITE_API_URL || '';
          const res = await fetchWithAuth(`${API_URL}/api/entreprise/me`);
          if (res.ok) {
            const entreprise = await res.json();
            localStorage.setItem('entreprise_id', entreprise.id);
          }
        } catch {
          // No entreprise — will be handled by setup modal
        }

        navigate('/saisie', { replace: true });
      } catch (err: unknown) {
        if (err instanceof AuthError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Authentication failed.');
        }
      }
    })();
  }, [navigate, searchParams, setAuthenticated]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center">
          <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="text-blue-600 hover:underline text-sm"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}
